
import React, { useRef } from "react";
import { getUserType } from "@/helpers/common";
import { INVESTOR_MEMBER_PLAN, USER_TYPE_AGENT } from "@/constants/common";
import HeaderSidebarAgent from "./HeaderSidebarAgent";
import HeaderSidebarConsumer from "./HeaderSidebarConsumer";
import { useHeaderLogic } from "@/hooks/useHeaderLogic";
import _ from "lodash";
import { pageURLs } from "@/constants";
import { useNavigate, NavLink } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import { LOGIN, SIGNUP } from "@/constants/onBoarding";
import { themeColors } from "@/themeColors";
import { USER_TYPE_MEMBER } from "@/constants/v2/common";
import { useUserDetails } from "@/context";
import Logo from "@/components/Common/Logo";
import Home from "@/images/v2/Home-icon.svg";
import { AuthButtons, CommonHeaderButtons } from "@/components/Common/BaseHeader";
import { helpURL } from "@/constants/endPoints";
import { useCurrentWebsite } from "@/hooks/useCurrentWebsite";
import "../../../styles/desktop/general.scss";
import "./HeaderSidebar.scss";
import useAgentGitchaRestrictions from "@/hooks/useAgentGitchaRestrictions";
import { useMLSAgentAtGitchaRedirect } from "@/hooks/useMLSAgentAtGitchaRedirect";
import { useAppConfig } from "@/appConfig";
import { isMlsRestrictedTenant } from "@/helpers/tenant";
import useIsMobile from "@/hooks/useIsMobile";

const HEADER_HEIGHT = 48;

export const BaseSidebarWrapper = ({ children, shouldToggleColor }) => {
  return (
    <div
      className={`sidebar-main ${shouldToggleColor ? "sidebar-main-premium" : ""}`}
      style={{
        marginTop: HEADER_HEIGHT - 1,
      }}
    >
      {children}
    </div>
  )
}

export const SidebarItem = ({
  to,
  onClick,
  icon = Home,
  label = "Feed",
  isActive = false,
}) => {
  const Wrapper = onClick ? "button" : NavLink;
  const props = onClick ? { onClick, type: "button" } : { to };

  return (
    <Wrapper
      {...props}
      className={`d-flex flex-column align-items-center sidebar-wrapper ${isActive ? "sidebar-wrapper-active" : ""
        }`}
      style={onClick ? { all: "unset", cursor: "pointer" } : undefined}
    >
      <div className="sidebar-menu">
        <img src={icon} alt="Gitcha Logo" className="sidebar-menu-img" />
      </div>
      <span className="sidebar-menu-list">{label}</span>
    </Wrapper>
  );
};

export const HeaderAvatar = ({ user }) => {
  const userInfo = user?.userInfo;
  if (!userInfo) return null;

  const { name = "", image = "" } = userInfo;
  const hasImage = Boolean(image);
  const initials = (name.replace(/-/g, "").match(/\b(\w)/g) || []).join("") || "";

  return hasImage ? (
    <img className="brdr-rs-200" src={image} alt="Header Avatar" />
  ) : (
    initials
  );
};

const HeaderSidebar = (props) => {

  const headerLogic = useHeaderLogic(props);

  const { isGitchaDefaultWebsite } = useCurrentWebsite()

  const allProps = { ...headerLogic, ...props };

  const { user = null, handleAgentOnboardingModal = () => { } } = allProps;

  const userType = getUserType();
  const isAgent = userType === USER_TYPE_AGENT;

  const showSidebarAuthenticatedItems =
    user &&
    Object.keys(_.get(user, "userInfo", {})).length &&
    _.get(user, "userInfo.planStatus", false) &&
    _.get(user, "userInfo.userStatus", false);

  const navigate = useNavigate();

  const goTo = (path) => navigate(path);

  const handleGoFeed = () => {
    if (window.location.pathname !== pageURLs.home) {
      goTo(pageURLs.home);
    }
  };

  const handleGoInventory = () => {
    if (!showSidebarAuthenticatedItems) {
      handleAgentOnboardingModal({ showPopup: true, userAction: isGitchaDefaultWebsite ? SIGNUP : LOGIN });
      return;
    }

    const path = isAgent ? pageURLs.agentInventoryListing : pageURLs.consumerInventoryListing;
    if (window.location.pathname !== path) {
      goTo(path);
    }
  }

  const sidebarProps = {
    headerHeight: HEADER_HEIGHT,
    handleGoFeed,
    handleGoInventory,
    goTo: goTo,
    isGitchaDefaultWebsite,
    ...allProps
  }

  if (isAgent) return <HeaderSidebarAgent {...sidebarProps} />

  return <HeaderSidebarConsumer {...sidebarProps} />
};

export const HeaderSidebarDropdownMenu = (props) => {

  const {
    user,
    userDetails,
    userPlan,
    showAgentMenuNotificationIcon,
    navigate,
    handleLogOut,
    PremiumPlanModalShow,
    BasicPlanModalShow,
  } = props;

  const { shouldRestrictAgent } = useAgentGitchaRestrictions()
  const { redirectToBLSEnv } = useMLSAgentAtGitchaRedirect();
  const { tenant } = useAppConfig() || {};
  const mlsRestricted = isMlsRestrictedTenant(tenant);

  const isAgent = _.get(user, "userInfo.userType", "") === USER_TYPE_AGENT;

  return (
    <div className="toplinks">
      <Dropdown>
        <Dropdown.Toggle
          variant=""
          id="dropdown-basic"
          className="profile-img profile-bg-green ml-6"
        >
          <HeaderAvatar user={user} />
        </Dropdown.Toggle>

        <Dropdown.Menu align="end" popperConfig={{ strategy: "fixed" }}>
          <Dropdown.Item onClick={() => navigate(pageURLs.settings)}>
            <span>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M10.0001 3.125C7.58386 3.125 5.6251 5.08375 5.6251 7.5C5.6251 9.91625 7.58386 11.875 10.0001 11.875C12.4164 11.875 14.3751 9.91625 14.3751 7.5C14.3751 5.08375 12.4164 3.125 10.0001 3.125ZM4.3751 7.5C4.3751 4.3934 6.8935 1.875 10.0001 1.875C13.1067 1.875 15.6251 4.3934 15.6251 7.5C15.6251 10.6066 13.1067 13.125 10.0001 13.125C6.8935 13.125 4.3751 10.6066 4.3751 7.5Z"
                  fill={themeColors.neutral900}
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M10.0001 13.1243C8.57374 13.1243 7.17251 13.4998 5.93728 14.213C4.70205 14.9263 3.67634 15.9521 2.96327 17.1875C2.79071 17.4864 2.40848 17.5889 2.10953 17.4163C1.81058 17.2437 1.70812 16.8615 1.88068 16.5626C2.70345 15.1372 3.88696 13.9535 5.31223 13.1305C6.7375 12.3076 8.3543 11.8743 10.0001 11.8743C11.6459 11.8743 13.2627 12.3076 14.688 13.1305C16.1132 13.9535 17.2968 15.1372 18.1195 16.5626C18.2921 16.8615 18.1896 17.2437 17.8907 17.4163C17.5917 17.5889 17.2095 17.4864 17.0369 17.1875C16.3239 15.9521 15.2982 14.9263 14.0629 14.213C12.8277 13.4998 11.4265 13.1243 10.0001 13.1243Z"
                  fill={themeColors.neutral900}
                />
              </svg>
            </span>
            Account
          </Dropdown.Item>

          {!shouldRestrictAgent && <Dropdown.Item onClick={() => navigate("/my-favorites")}>
            <span>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M7.75312 4.35487C7.0517 3.98137 6.24254 3.86242 5.4633 4.01827C4.68407 4.17412 3.9829 4.59513 3.47909 5.20968C2.97528 5.82423 2.69995 6.59435 2.69995 7.38901C2.69995 9.7576 4.46368 12.0183 6.3993 13.7604C7.34821 14.6144 8.29998 15.3095 9.01582 15.7915C9.37314 16.0321 9.67017 16.2186 9.87668 16.3442C9.8803 16.3464 9.88389 16.3486 9.88745 16.3508C9.89101 16.3486 9.89461 16.3464 9.89823 16.3442C10.1047 16.2186 10.4018 16.0321 10.7591 15.7915C11.4749 15.3095 12.4267 14.6144 13.3756 13.7604C15.3112 12.0183 17.075 9.7576 17.075 7.38901C17.075 6.59435 16.7996 5.82423 16.2958 5.20968C15.792 4.59513 15.0908 4.17412 14.3116 4.01827C13.5324 3.86242 12.7232 3.98137 12.0218 4.35487C11.3204 4.72837 10.77 5.33336 10.4644 6.0669C10.3673 6.2998 10.1398 6.45151 9.88745 6.45151C9.63514 6.45151 9.40757 6.2998 9.31053 6.0669C9.00489 5.33336 8.45454 4.72837 7.75312 4.35487ZM9.88745 17.0765C9.58207 17.6218 9.58191 17.6217 9.58171 17.6216L9.57954 17.6204L9.57418 17.6174L9.55514 17.6066C9.53882 17.5972 9.5153 17.5837 9.48506 17.5661C9.4246 17.5309 9.33725 17.4792 9.22684 17.4121C9.0061 17.2777 8.69277 17.0809 8.31767 16.8283C7.56868 16.324 6.56731 15.5933 5.5631 14.6895C3.59248 12.916 1.44995 10.3329 1.44995 7.38901C1.44995 6.30538 1.8254 5.25521 2.51241 4.41719C3.19942 3.57917 4.15556 3.00506 5.21816 2.79254C6.28075 2.58002 7.38415 2.74222 8.34063 3.25155C8.95128 3.57671 9.47796 4.03054 9.88745 4.57651C10.2969 4.03054 10.8236 3.57671 11.4343 3.25155C12.3907 2.74222 13.4942 2.58002 14.5567 2.79254C15.6193 3.00506 16.5755 3.57917 17.2625 4.41719C17.9495 5.25521 18.325 6.30538 18.325 7.38901C18.325 10.3329 16.1824 12.916 14.2118 14.6895C13.2076 15.5933 12.2062 16.324 11.4572 16.8283C11.0821 17.0809 10.7688 17.2777 10.5481 17.4121C10.4377 17.4792 10.3503 17.5309 10.2898 17.5661C10.2596 17.5837 10.2361 17.5972 10.2198 17.6066L10.2007 17.6174L10.1954 17.6204L10.1937 17.6213C10.1935 17.6214 10.1928 17.6218 9.88745 17.0765ZM9.88745 17.0765L10.1928 17.6218C10.0031 17.7281 9.77143 17.7279 9.58171 17.6216L9.88745 17.0765Z"
                  fill={themeColors.neutral900}
                />
              </svg>
            </span>
            Favorites
          </Dropdown.Item>
          }

          {userDetails?.period === 1 && (
            <Dropdown.Item
              onClick={() =>
                _.get(user, "userInfo.userType", "") == USER_TYPE_AGENT
                  ? PremiumPlanModalShow()
                  : BasicPlanModalShow()
              }
            >
              <span>
                <svg width="20" height="20" viewBox="0 0 20 20">
                  <path
                    d="M16.385,11.269a1.233,1.233,0,0,1-.814,1.167l-4.035,1.484-1.484,4.032a1.244,1.244,0,0,1-2.334,0L6.229,13.926,2.2,12.441a1.244,1.244,0,0,1,0-2.334L6.231,8.622,7.715,4.59a1.244,1.244,0,0,1,2.334,0l1.484,4.035,4.032,1.484a1.233,1.233,0,0,1,.819,1.159Zm-4.375-7.5h1.25v1.25a.625.625,0,1,0,1.25,0V3.769h1.25a.625.625,0,1,0,0-1.25H14.51V1.269a.625.625,0,0,0-1.25,0v1.25H12.01a.625.625,0,0,0,0,1.25Zm6.875,2.5H18.26V5.644a.625.625,0,0,0-1.25,0v.625h-.625a.625.625,0,0,0,0,1.25h.625v.625a.625.625,0,1,0,1.25,0V7.519h.625a.625.625,0,1,0,0-1.25Z"
                    transform="translate(-0.383 0.356)"
                    fill="#234d93"
                  />
                </svg>
              </span>
              Upgrade to {isAgent ? "Pro Member" : "Member"}
            </Dropdown.Item>
          )}

          <Dropdown.Divider />

          {_.get(user, "userInfo.userType", "") === USER_TYPE_MEMBER &&
            userPlan === INVESTOR_MEMBER_PLAN && (
              <Dropdown.Item
                onClick={() => navigate("/investor-dashboard")}
                className="position-relative"
              >
                {showAgentMenuNotificationIcon && (
                  <div className="ring-container-db">
                    <div className="circlebtn">
                      <div className="ringring"></div>
                    </div>
                  </div>
                )}
                <span>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M6.49922 13.625C6.5649 13.6744 6.63968 13.7103 6.71926 13.7307C6.79885 13.7512 6.88169 13.7557 6.96303 13.7441C7.04437 13.7325 7.12263 13.705 7.19332 13.6631C7.26401 13.6212 7.32575 13.5658 7.375 13.5C7.75341 12.9955 8.24409 12.5859 8.8082 12.3039C9.3723 12.0218 9.99432 11.875 10.625 11.875C11.2557 11.875 11.8777 12.0218 12.4418 12.3039C13.0059 12.5859 13.4966 12.9955 13.875 13.5C13.9242 13.5657 13.9859 13.621 14.0566 13.6628C14.1272 13.7046 14.2054 13.7321 14.2866 13.7437C14.3679 13.7553 14.4506 13.7508 14.5301 13.7304C14.6096 13.7101 14.6843 13.6742 14.75 13.625C14.8157 13.5758 14.871 13.5141 14.9128 13.4434C14.9546 13.3728 14.9821 13.2946 14.9937 13.2134C15.0053 13.1321 15.0008 13.0494 14.9804 12.9699C14.9601 12.8904 14.9242 12.8157 14.875 12.75C14.3216 12.008 13.5836 11.4238 12.7344 11.0555C13.1998 10.6306 13.5258 10.0749 13.6697 9.4614C13.8137 8.84789 13.7688 8.2052 13.5409 7.61767C13.313 7.03014 12.9129 6.52523 12.3929 6.1692C11.873 5.81316 11.2575 5.62265 10.6273 5.62265C9.99718 5.62265 9.38173 5.81316 8.86178 6.1692C8.34182 6.52523 7.94166 7.03014 7.71379 7.61767C7.48593 8.2052 7.44102 8.84789 7.58495 9.4614C7.72888 10.0749 8.05493 10.6306 8.52031 11.0555C7.6694 11.4231 6.92972 12.0074 6.375 12.75C6.27546 12.8825 6.23263 13.0491 6.25592 13.2132C6.27922 13.3773 6.36673 13.5254 6.49922 13.625ZM8.75 8.75C8.75 8.37916 8.85997 8.01665 9.06599 7.70831C9.27202 7.39996 9.56486 7.15964 9.90747 7.01773C10.2501 6.87581 10.6271 6.83868 10.9908 6.91103C11.3545 6.98337 11.6886 7.16195 11.9508 7.42417C12.213 7.6864 12.3916 8.02049 12.464 8.38421C12.5363 8.74792 12.4992 9.12492 12.3573 9.46753C12.2154 9.81014 11.975 10.103 11.6667 10.309C11.3584 10.515 10.9958 10.625 10.625 10.625C10.1277 10.625 9.65081 10.4275 9.29917 10.0758C8.94754 9.72419 8.75 9.24728 8.75 8.75ZM16.25 1.875H5C4.66848 1.875 4.35054 2.0067 4.11612 2.24112C3.8817 2.47554 3.75 2.79348 3.75 3.125V5H2.5C2.33424 5 2.17527 5.06585 2.05806 5.18306C1.94085 5.30027 1.875 5.45924 1.875 5.625C1.875 5.79076 1.94085 5.94973 2.05806 6.06694C2.17527 6.18415 2.33424 6.25 2.5 6.25H3.75V9.375H2.5C2.33424 9.375 2.17527 9.44085 2.05806 9.55806C1.94085 9.67527 1.875 9.83424 1.875 10C1.875 10.1658 1.94085 10.3247 2.05806 10.4419C2.17527 10.5592 2.33424 10.625 2.5 10.625H3.75V13.75H2.5C2.33424 13.75 2.17527 13.8158 2.05806 13.9331C1.94085 14.0503 1.875 14.2092 1.875 14.375C1.875 14.5408 1.94085 14.6997 2.05806 14.8169C2.17527 14.9342 2.33424 15 2.5 15H3.75V16.875C3.75 17.2065 3.8817 17.5245 4.11612 17.7589C4.35054 17.9933 4.66848 18.125 5 18.125H16.25C16.5815 18.125 16.8995 17.9933 17.1339 17.7589C17.3683 17.5245 17.5 17.2065 17.5 16.875V3.125C17.5 2.79348 17.3683 2.47554 17.1339 2.24112C16.8995 2.0067 16.5815 1.875 16.25 1.875ZM16.25 16.875H5V3.125H16.25V16.875Z"
                      fill="black"
                    />
                  </svg>
                </span>
                Investor dashboard
              </Dropdown.Item>
            )}

          <Dropdown.Item onClick={() => {
            const path = isAgent ? pageURLs.agentInventoryListing : pageURLs.consumerInventoryListing;
            redirectToBLSEnv({ path, onGitchaEnvFallback: () => navigate(path) });
          }}>
            <span>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M10 2.08984C9.52138 2.08984 9.04293 2.24553 8.64746 2.55697H8.64665L3.69141 6.4624C2.93992 7.05464 2.5 7.95958 2.5 8.91683V16.4535C2.5 17.2513 3.16044 17.9118 3.95833 17.9118H16.0417C16.8396 17.9118 17.5 17.2513 17.5 16.4535V8.91683C17.5 7.95958 17.0601 7.05464 16.3086 6.4624L16.25 6.4152V3.125C16.25 2.95924 16.1841 2.80028 16.0669 2.68308C15.9497 2.56587 15.7908 2.50002 15.625 2.5H13.5417C13.3759 2.50002 13.2169 2.56587 13.0997 2.68308C12.9825 2.80028 12.9167 2.95924 12.9167 3.125V3.78906L11.3525 2.55697C10.9571 2.24553 10.4786 2.08984 10 2.08984ZM10 3.33659C10.2041 3.33659 10.4082 3.40441 10.5794 3.53923L13.1551 5.56803C13.2473 5.64054 13.358 5.68566 13.4746 5.69824C13.5911 5.71082 13.7089 5.69035 13.8144 5.63917C13.9199 5.58798 14.0089 5.50815 14.0712 5.4088C14.1335 5.30945 14.1666 5.19458 14.1667 5.07731V3.75H15V6.71875C15.0001 6.81325 15.0216 6.9065 15.0629 6.9915C15.1042 7.0765 15.1642 7.15102 15.2384 7.20947L15.5355 7.44385C15.9873 7.79994 16.25 8.34158 16.25 8.91683V16.4535C16.25 16.5764 16.1646 16.6618 16.0417 16.6618H3.95833C3.8354 16.6618 3.75 16.5764 3.75 16.4535V8.91683C3.75 8.34158 4.01267 7.79994 4.46452 7.44385L9.42057 3.53923C9.59177 3.40441 9.79592 3.33659 10 3.33659ZM6.04167 9.99512C5.87591 9.99513 5.71695 10.061 5.59974 10.1782C5.48254 10.2954 5.41668 10.4544 5.41667 10.6201V13.9535C5.41668 14.1192 5.48254 14.2782 5.59974 14.3954C5.71695 14.5126 5.87591 14.5784 6.04167 14.5785H8.54167C8.70742 14.5784 8.86638 14.5126 8.98359 14.3954C9.1008 14.2782 9.16665 14.1192 9.16667 13.9535V10.6201C9.16665 10.4544 9.1008 10.2954 8.98359 10.1782C8.86638 10.061 8.70742 9.99513 8.54167 9.99512H6.04167ZM11.4583 9.99512C11.2926 9.99513 11.1336 10.061 11.0164 10.1782C10.8992 10.2954 10.8333 10.4544 10.8333 10.6201V13.9535C10.8333 14.1192 10.8992 14.2782 11.0164 14.3954C11.1336 14.5126 11.2926 14.5784 11.4583 14.5785H13.9583C14.1241 14.5784 14.283 14.5126 14.4003 14.3954C14.5175 14.2782 14.5833 14.1192 14.5833 13.9535V10.6201C14.5833 10.4544 14.5175 10.2954 14.4003 10.1782C14.283 10.061 14.1241 9.99513 13.9583 9.99512H11.4583ZM6.66667 11.2451H7.91667V13.3285H6.66667V11.2451ZM12.0833 11.2451H13.3333V13.3285H12.0833V11.2451Z"
                  fill="#333A48"
                />
              </svg>
            </span>
            My Inventory
          </Dropdown.Item>

          {_.get(user, "userInfo.userType", "") === USER_TYPE_AGENT && <Dropdown.Item onClick={() => navigate(pageURLs.agentMyWantAdListings)}>
            <span>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M2.26407 3.05319C2.49849 2.81877 2.81643 2.68707 3.14795 2.68707H16.8979C17.2295 2.68707 17.5474 2.81877 17.7818 3.05319C18.0163 3.28761 18.1479 3.60555 18.1479 3.93707V16.4371C18.1479 16.6537 18.0358 16.8549 17.8515 16.9687C17.6673 17.0826 17.4372 17.093 17.2434 16.9961L15.0229 15.8858L12.8025 16.9961C12.6265 17.0841 12.4194 17.0841 12.2434 16.9961L10.0229 15.8858L7.80246 16.9961C7.6265 17.0841 7.41939 17.0841 7.24344 16.9961L5.02295 15.8858L2.80246 16.9961C2.60871 17.093 2.37863 17.0826 2.19437 16.9687C2.01011 16.8549 1.89795 16.6537 1.89795 16.4371V3.93707C1.89795 3.60555 2.02965 3.28761 2.26407 3.05319ZM16.8979 3.93707L3.14795 3.93707L3.14795 15.4258L4.74344 14.6281C4.9194 14.5401 5.1265 14.5401 5.30246 14.6281L7.52295 15.7383L9.74344 14.6281C9.9194 14.5401 10.1265 14.5401 10.3025 14.6281L12.5229 15.7383L14.7434 14.6281C14.9194 14.5401 15.1265 14.5401 15.3025 14.6281L16.8979 15.4258V3.93707Z"
                  fill="#333A48"
                />
              </svg>
            </span>
            My Want-Listings
          </Dropdown.Item>}

          <Dropdown.Item href={void 0} onClick={() => { window.location.href = helpURL; }}>
            <span>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M10.9375 14.0625C10.9375 14.2479 10.8825 14.4292 10.7795 14.5833C10.6765 14.7375 10.5301 14.8577 10.3588 14.9286C10.1875 14.9996 9.99896 15.0182 9.81711 14.982C9.63525 14.9458 9.4682 14.8565 9.33709 14.7254C9.20598 14.5943 9.11669 14.4273 9.08052 14.2454C9.04434 14.0635 9.06291 13.875 9.13387 13.7037C9.20482 13.5324 9.32499 13.386 9.47916 13.283C9.63333 13.18 9.81458 13.125 10 13.125C10.2486 13.125 10.4871 13.2238 10.6629 13.3996C10.8387 13.5754 10.9375 13.8139 10.9375 14.0625ZM10 5.625C8.27657 5.625 6.875 6.88672 6.875 8.4375V8.75C6.875 8.91576 6.94085 9.07473 7.05806 9.19194C7.17527 9.30915 7.33424 9.375 7.5 9.375C7.66576 9.375 7.82474 9.30915 7.94195 9.19194C8.05916 9.07473 8.125 8.91576 8.125 8.75V8.4375C8.125 7.57812 8.96641 6.875 10 6.875C11.0336 6.875 11.875 7.57812 11.875 8.4375C11.875 9.29688 11.0336 10 10 10C9.83424 10 9.67527 10.0658 9.55806 10.1831C9.44085 10.3003 9.375 10.4592 9.375 10.625V11.25C9.375 11.4158 9.44085 11.5747 9.55806 11.6919C9.67527 11.8092 9.83424 11.875 10 11.875C10.1658 11.875 10.3247 11.8092 10.4419 11.6919C10.5592 11.5747 10.625 11.4158 10.625 11.25V11.1938C12.05 10.932 13.125 9.79531 13.125 8.4375C13.125 6.88672 11.7234 5.625 10 5.625ZM18.125 10C18.125 11.607 17.6485 13.1779 16.7557 14.514C15.8629 15.8502 14.594 16.8916 13.1093 17.5065C11.6247 18.1215 9.99099 18.2824 8.4149 17.9689C6.8388 17.6554 5.39106 16.8815 4.25476 15.7452C3.11846 14.6089 2.34463 13.1612 2.03112 11.5851C1.71762 10.009 1.87852 8.37535 2.49348 6.8907C3.10844 5.40605 4.14985 4.1371 5.486 3.24431C6.82214 2.35152 8.39303 1.875 10 1.875C12.1542 1.87727 14.2195 2.73403 15.7427 4.25727C17.266 5.78051 18.1227 7.84581 18.125 10ZM16.875 10C16.875 8.64025 16.4718 7.31104 15.7164 6.18045C14.9609 5.04987 13.8872 4.16868 12.631 3.64833C11.3747 3.12798 9.99238 2.99183 8.65876 3.2571C7.32514 3.52237 6.10013 4.17716 5.13864 5.13864C4.17716 6.10013 3.52238 7.32513 3.2571 8.65875C2.99183 9.99237 3.12798 11.3747 3.64833 12.6309C4.16868 13.8872 5.04987 14.9609 6.18046 15.7164C7.31105 16.4718 8.64026 16.875 10 16.875C11.8227 16.8729 13.5702 16.1479 14.8591 14.8591C16.1479 13.5702 16.8729 11.8227 16.875 10Z"
                  fill="black"
                />
              </svg>
            </span>
            Help
          </Dropdown.Item>

          {/* TEMPORARY: legal links hidden — flip `false` back to `mlsRestricted` to restore */}
          {false && mlsRestricted && (
            <>
              <Dropdown.Divider />
              <Dropdown.Item
                href="https://gitcha.com/terms-of-service"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Use
              </Dropdown.Item>
              <Dropdown.Item
                href="https://gitcha.com/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </Dropdown.Item>
            </>
          )}

          <Dropdown.Item onClick={() => handleLogOut()}>
            <span>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M8.75 16.875C8.75 17.0408 8.68415 17.1997 8.56694 17.3169C8.44973 17.4342 8.29076 17.5 8.125 17.5H3.75C3.41848 17.5 3.10054 17.3683 2.86612 17.1339C2.6317 16.8995 2.5 16.5815 2.5 16.25V3.75C2.5 3.41848 2.6317 3.10054 2.86612 2.86612C3.10054 2.6317 3.41848 2.5 3.75 2.5H8.125C8.29076 2.5 8.44973 2.56585 8.56694 2.68306C8.68415 2.80027 8.75 2.95924 8.75 3.125C8.75 3.29076 8.68415 3.44973 8.56694 3.56694C8.44973 3.68415 8.29076 3.75 8.125 3.75H3.75V16.25H8.125C8.29076 16.25 8.44973 16.3158 8.56694 16.4331C8.68415 16.5503 8.75 16.7092 8.75 16.875ZM17.3172 9.55781L14.1922 6.43281C14.0749 6.31554 13.9159 6.24965 13.75 6.24965C13.5841 6.24965 13.4251 6.31554 13.3078 6.43281C13.1905 6.55009 13.1247 6.70915 13.1247 6.875C13.1247 7.04085 13.1905 7.19991 13.3078 7.31719L15.3664 9.375H8.125C7.95924 9.375 7.80027 9.44085 7.68306 9.55806C7.56585 9.67527 7.5 9.83424 7.5 10C7.5 10.1658 7.56585 10.3247 7.68306 10.4419C7.80027 10.5592 7.95924 10.625 8.125 10.625H15.3664L13.3078 12.6828C13.1905 12.8001 13.1247 12.9591 13.1247 13.125C13.1247 13.2909 13.1905 13.4499 13.3078 13.5672C13.4251 13.6845 13.5841 13.7503 13.75 13.7503C13.9159 13.7503 14.0749 13.6845 14.1922 13.5672L17.3172 10.4422C17.3753 10.3841 17.4214 10.3152 17.4529 10.2393C17.4843 10.1635 17.5005 10.0821 17.5005 10C17.5005 9.91787 17.4843 9.83654 17.4529 9.76066C17.4214 9.68479 17.3753 9.61586 17.3172 9.55781Z"
                  fill="black"
                />
              </svg>
            </span>
            Log Out
          </Dropdown.Item>

        </Dropdown.Menu>


      </Dropdown>
    </div>
  )

}

export const HeaderSidebarBaseWrapper = (props) => {

  const { user, headerExtraButtons, headerSidebarDropdownMenuProps, authButtonsProps, headerHeight } = props

  const headerRef = useRef(null);
  const navigate = useNavigate();

  const { isGitchaDefaultWebsite } = useCurrentWebsite();
  const { shouldToggleColor } = useUserDetails();
  const isMobile = useIsMobile()

  const usePremiumStyles = shouldToggleColor && !(isGitchaDefaultWebsite && isMobile);
  const _logo = usePremiumStyles ? "logoWhite" : "logoV1";

  return (
    <>
      <div
        className={`top-header ${usePremiumStyles ? "top-header-premium" : ""} ${isGitchaDefaultWebsite ? "top-header-default" : ""} ${!_.get(user, "userInfo.userId") ? "logged-out" : ""}`}
        ref={headerRef}
      >
        <div className="w-100 h-100 d-flex p-0 flex-column flex-sm-column flex-md-row justify-content-between align-items-center">
          <div className="d-flex align-items-baseline">
            <div className="brand-tagline mb-3 mb-md-0 mt-1 mt-lg-0 cust-fsize-small">
              <div className="navbar-brand p-0 me-lg-3">
                <a
                  href={void 0}
                  className="d-block text-decoration-none"
                  onClick={() => {
                    if (window.location.pathname !== pageURLs.home) {
                      navigate("/home");
                      if (props.fromHome) {
                        props.redirectToHomePage();
                      }
                    }
                  }}
                >
                  <Logo type={_logo} />
                </a>
              </div>
            </div>
          </div>

          <ul className="d-flex flex-row navbar-nav flex-column flex-md-row header-sidebar-row-responsive">
            <li className="nav-item d-flex flex-row justify-content-center mb-md-0 icons-container">
              <CommonHeaderButtons
                user={user}
                headerLogic={props}
                isPremium={usePremiumStyles}
              />
            </li>

            <li className="nav-item d-flex flex-row">
              <div className="top-btn-wrap d-flex">
                {headerExtraButtons && headerExtraButtons()}
                {user &&
                  Object.keys(_.get(user, "userInfo", {})).length &&
                  _.get(user, "userInfo.planStatus", false) &&
                  _.get(user, "userInfo.userStatus", false) ? (
                  <HeaderSidebarDropdownMenu {...headerSidebarDropdownMenuProps} />
                ) : (
                  <AuthButtons showSignUpButton={isGitchaDefaultWebsite} {...authButtonsProps} />
                )}
              </div>
            </li>
          </ul>
        </div>
      </div>

      <div style={{ paddingTop: headerHeight + (isMobile ? 56 : 72) }}></div>

      {!isGitchaDefaultWebsite && (
        <div className="left-sidebar p-0">
          {props?.sidebar}
        </div>
      )}

      {props?.mobileNav}
    </>
  )
}

export default HeaderSidebar;
